import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CalendarIcon,
  TrophyIcon,
  Bars3Icon,
  InformationCircleIcon,
  UserGroupIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  TrophyIcon as TrophyIconSolid,
  Bars3Icon as Bars3IconSolid,
  InformationCircleIcon as InfoSolid,
  UserGroupIcon as UserGroupSolid,
  ClockIcon as ClockSolid,
  ClipboardDocumentListIcon as ClipboardSolid,
} from '@heroicons/react/24/solid';
import { supabase } from '../lib/supabase';

interface NavTab {
  label: string;
  path: string;
  OutlineIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  SolidIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const TAB_HOME: NavTab = { label: 'Home', path: '/m', OutlineIcon: HomeIcon, SolidIcon: HomeIconSolid };
const TAB_CALENDAR: NavTab = { label: 'Schedule', path: '/m/calendar', OutlineIcon: CalendarIcon, SolidIcon: CalendarIconSolid };
const TAB_LEADERBOARD: NavTab = { label: 'Leaderboard', path: '/m/leaderboard', OutlineIcon: TrophyIcon, SolidIcon: TrophyIconSolid };
const TAB_HISTORY: NavTab = { label: 'History', path: '/m/history', OutlineIcon: ClockIcon, SolidIcon: ClockSolid };
const TAB_PARTNERS: NavTab = { label: 'Partners', path: '/m/sponsors', OutlineIcon: UserGroupIcon, SolidIcon: UserGroupSolid };
const TAB_REGISTER: NavTab = { label: 'Register', path: '/m/registration', OutlineIcon: ClipboardDocumentListIcon, SolidIcon: ClipboardSolid };
const TAB_MORE: NavTab = { label: 'More', path: '/m/more', OutlineIcon: Bars3Icon, SolidIcon: Bars3IconSolid };
const TAB_ABOUT: NavTab = { label: 'About', path: '/m/about', OutlineIcon: InformationCircleIcon, SolidIcon: InfoSolid };

export default function MobileNavBar() {
  const location = useLocation();
  const [settings, setSettings] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['history_published', 'leaderboard_published', 'registration_enabled']),
      supabase
        .from('tournaments')
        .select('registration_open_date')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle(),
    ]).then(([settingsRes, tournamentRes]) => {
      const map: Record<string, string | boolean> = {};
      (settingsRes.data ?? []).forEach(row => { map[row.key] = row.value; });

      const historyOn = map['history_published'] !== false && map['history_published'] !== 'false';
      const leaderboardOn = map['leaderboard_published'] === true || map['leaderboard_published'] === 'true';
      const regEnabled = map['registration_enabled'] !== false && map['registration_enabled'] !== 'false';
      let regOpen = false;
      if (regEnabled && tournamentRes.data?.registration_open_date) {
        regOpen = new Date() >= new Date(tournamentRes.data.registration_open_date);
      }

      setSettings({ historyOn, leaderboardOn, regOpen });
      setLoaded(true);
    });
  }, []);

  const buildTabs = (): NavTab[] => {
    if (!loaded) {
      return [TAB_HOME, TAB_CALENDAR, TAB_HISTORY, TAB_PARTNERS, TAB_MORE];
    }

    const { historyOn, leaderboardOn, regOpen } = settings;

    const slot2 = TAB_CALENDAR;

    // Slot 3: Leaderboard when active, else History when published, else About
    let slot3: NavTab;
    if (leaderboardOn) slot3 = TAB_LEADERBOARD;
    else if (historyOn) slot3 = TAB_HISTORY;
    else slot3 = TAB_ABOUT;

    // Slot 4: Register when registration is open, else Partners
    const slot4: NavTab = regOpen ? TAB_REGISTER : TAB_PARTNERS;

    return [TAB_HOME, slot2, slot3, slot4, TAB_MORE];
  };

  const tabs = buildTabs();

  const isActive = (path: string) => {
    if (path === '/m') return location.pathname === '/m' || location.pathname === '/m/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 safe-area-inset-bottom"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-stretch">
        {tabs.map((tab) => {
          const active = isActive(tab.path);
          const Icon = active ? tab.SolidIcon : tab.OutlineIcon;
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`flex flex-col items-center justify-center flex-1 py-2 gap-0.5 transition-colors ${
                active ? 'text-golf-orange' : 'text-gray-400'
              }`}
            >
              <Icon className="w-6 h-6" />
              <span className={`text-[10px] font-medium leading-tight ${active ? 'text-golf-orange' : 'text-gray-400'}`}>
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
